import { initComponent } from "../../utilities/common";
import { scrollIntoViewIfNeeded } from "../scroll-logic/browser";
import formDataEntries from "../form-components/formEntries";
import formStyles from "../form-components/form-styles/styles.css";

class PostSubmitDataGathering {
  constructor(element) {
    this.element = element;
    this.form = element.querySelector("form");
    this.formState = element.querySelector("[data-post-submit-form-state]");
    this.successState = element.querySelector("[data-post-submit-success]");
    this.errorState = element.querySelector("[data-post-submit-error]");
    this.errorButton = this.errorState?.querySelector('[data-type="button"]');
    this.submitButton = element.querySelector('[type="submit"]');
    this.rejectButton = element.querySelector("[data-post-submit-reject]");
    this.submitting = false;

    this.setupListeners();
  }

  setupListeners() {
    this.form?.addEventListener("submit", (event) => this.handleSubmit(event));
    this.errorButton?.addEventListener("click", () => this.hideErrorState());
    this.rejectButton?.addEventListener("click", () => this.dismiss());
    this.form?.querySelectorAll("input, select, textarea").forEach((field) => {
      field.addEventListener("focus", () => {
        field.classList.remove(formStyles.valid, formStyles.invalid);
      });
    });
  }

  async handleSubmit(event) {
    event.preventDefault();
    if (this.submitting) return;

    this.hideError();

    const isValid = await this.validateForm();
    if (!isValid) return;

    const payload = this.buildPayload();
    if (!PostSubmitDataGathering.hasCollectedData(payload)) {
      this.showSuccess();
      return;
    }

    this.setSubmitting(true);

    try {
      const response = await fetch(
        `${window.PUBLIC_ENV.CONSOLIDATION_PREFIX}/api/post-submit`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      this.showSuccess();
    } catch {
      this.showError();
    } finally {
      this.setSubmitting(false);
    }
  }

  async validateForm() {
    if (!this.form) return false;

    const allInputs = [
      ...this.form.querySelectorAll(
        `.${formStyles.inputField}, [data-type="select-group"]`
      )
    ];
    const customValidationElements = [
      ...this.form.querySelectorAll(`.${formStyles.formInputs}`)
    ].filter((element) => typeof element.checkIsValid === "function");

    const { phase1Elements, phase2Elements } =
      PostSubmitDataGathering.categorizeElements(
        allInputs,
        customValidationElements
      );

    const phase1Results = await PostSubmitDataGathering.validatePhase(
      phase1Elements
    );
    if (
      PostSubmitDataGathering.hasValidationErrors(phase1Results, phase1Elements)
    ) {
      PostSubmitDataGathering.scrollToFirstInvalidField(
        phase1Elements,
        phase1Results
      );
      return false;
    }

    const phase2Results = await PostSubmitDataGathering.validatePhase(
      phase2Elements
    );
    if (
      PostSubmitDataGathering.hasValidationErrors(phase2Results, phase2Elements)
    ) {
      PostSubmitDataGathering.scrollToFirstInvalidField(
        phase2Elements,
        phase2Results
      );
      return false;
    }

    return true;
  }

  buildPayload() {
    const entries = formDataEntries(this.form);
    const formData = entries.reduce((prev, [key, value]) => {
      if (key === "dynamoGuid") return prev;
      return { ...prev, [key]: value };
    }, {});
    const surveyField = this.getSurveyField();

    return {
      transactionId: formData.transactionId || "",
      email: formData.email || "",
      surveyDataKey: surveyField?.key || "",
      surveyResponse: surveyField?.value || ""
    };
  }

  getSurveyField() {
    const selectGroup = this.form?.querySelector('[data-type="select-group"]');
    if (selectGroup) {
      const selectedInput = selectGroup.querySelector(
        'input[type="radio"]:checked'
      );
      const key =
        selectGroup.querySelector('[class*="heading"]')?.textContent?.trim() ||
        "";

      return {
        key,
        value: selectedInput?.value || ""
      };
    }

    const selectBox = this.form?.querySelector('[data-type="select-box"]');
    if (selectBox) {
      const select = selectBox.querySelector("select");
      const key = selectBox.querySelector("label")?.textContent?.trim() || "";

      return {
        key,
        value: select?.value || ""
      };
    }

    return null;
  }

  static hasCollectedData(payload) {
    return [payload.email, payload.surveyDataKey, payload.surveyResponse].some(
      (value) => value && value !== ""
    );
  }

  setSubmitting(isSubmitting) {
    this.submitting = isSubmitting;
    if (this.submitButton) {
      this.submitButton.disabled = isSubmitting;
    }
  }

  showSuccess() {
    this.formState.hidden = true;
    this.errorState.hidden = true;
    this.successState.hidden = false;
  }

  showError() {
    this.formState.hidden = true;
    this.successState.hidden = true;
    this.errorState.hidden = false;
  }

  hideError() {
    this.errorState.hidden = true;
  }

  hideErrorState() {
    this.errorState.hidden = true;
    this.successState.hidden = true;
    this.formState.hidden = false;
    scrollIntoViewIfNeeded(this.element, {
      behavior: "smooth",
      block: "center"
    });
  }

  dismiss() {
    this.element.hidden = true;
  }

  static hasValidationErrors(validationResults, elements) {
    const validatedElements = new Set();

    validationResults.forEach((result) => {
      if (
        result.status === "fulfilled" &&
        result.value?.valid &&
        result.value?.element
      ) {
        validatedElements.add(result.value.element);
      }
    });

    const failedValidations = validationResults.filter(
      (result) =>
        result.status === "fulfilled" && result.value && !result.value.valid
    );

    const firstInvalidElement = elements.find((element) => {
      if (validatedElements.has(element)) return false;

      if (
        element.tagName === "INPUT" ||
        element.tagName === "SELECT" ||
        element.tagName === "TEXTAREA"
      ) {
        return !element.checkValidity?.();
      }

      return element.getAttribute?.("data-validity") === "false";
    });

    return !!firstInvalidElement || failedValidations.length > 0;
  }

  static scrollToFirstInvalidField(elements, validationResults) {
    const failedValidation = validationResults.find(
      (result) =>
        result.status === "fulfilled" && result.value && !result.value.valid
    );

    const firstInvalidElement =
      elements.find((element) => {
        if (
          element.tagName === "INPUT" ||
          element.tagName === "SELECT" ||
          element.tagName === "TEXTAREA"
        ) {
          return !element.checkValidity?.();
        }

        return element.getAttribute?.("data-validity") === "false";
      }) || failedValidation?.value?.element;

    if (!firstInvalidElement) return;

    scrollIntoViewIfNeeded(
      firstInvalidElement.previousElementSibling || firstInvalidElement,
      {
        behavior: "smooth",
        block: "center"
      }
    );
  }

  static categorizeElements(allInputs, customValidationElements) {
    const phase1Elements = [];
    const phase2Elements = [];

    const isAddressField = (element) => {
      if (element.name) {
        const lowerName = element.name.toLowerCase();
        if (lowerName.includes("addr") || lowerName.includes("address")) {
          return true;
        }
      }

      return (
        element.getAttribute("data-type") === "address-block" ||
        element.closest('[data-type="address-block"]')
      );
    };

    const hasAddressContent = (element) => {
      if (element.value?.trim()) return true;

      const addressInputs = element.querySelectorAll(
        'input[name*="addr"], input[name*="address"]'
      );

      return Array.from(addressInputs).some((input) => input.value?.trim());
    };

    const addToPhase = (element) => {
      if (isAddressField(element) && hasAddressContent(element)) {
        if (!phase2Elements.includes(element)) {
          phase2Elements.push(element);
        }
      } else if (!phase1Elements.includes(element)) {
        phase1Elements.push(element);
      }
    };

    [...allInputs, ...customValidationElements].forEach(addToPhase);

    return { phase1Elements, phase2Elements };
  }

  static async validatePhase(elements) {
    const elementsWithValidation = elements.filter(
      (element) => typeof element.checkIsValid === "function"
    );

    const validationPromises = elementsWithValidation.map(async (element) => {
      try {
        const result = element.checkIsValid();

        if (result && typeof result.then === "function") {
          await result;
          return { element, valid: true };
        }

        return { element, valid: !!result };
      } catch (error) {
        return { element, valid: false, error };
      }
    });

    const results = await Promise.allSettled(validationPromises);

    elements.forEach((element) => {
      if (
        element.tagName === "INPUT" ||
        element.tagName === "SELECT" ||
        element.tagName === "TEXTAREA"
      ) {
        PostSubmitDataGathering.setElementValidityState(
          element,
          element.checkValidity?.()
        );
        return;
      }

      const hasCustomValidation =
        typeof element.checkIsValid === "function" &&
        element.getAttribute?.("data-validity");

      if (!hasCustomValidation) return;

      const isValid = element.getAttribute("data-validity") !== "false";
      const targetField =
        element.querySelector(`.${formStyles.inputField}`) ||
        element.querySelector("input, select, textarea");

      if (targetField) {
        PostSubmitDataGathering.setElementValidityState(targetField, isValid);
      }
    });

    return results;
  }

  static setElementValidityState(element, isValid) {
    if (!element) return;

    element.dataset.validity = isValid ? "true" : "false";
    element.classList.toggle(formStyles.valid, !!isValid);
    element.classList.toggle(formStyles.invalid, !isValid);
  }
}

export const init = () => {
  initComponent(
    "post-submit-data-gathering",
    (element) => new PostSubmitDataGathering(element)
  );
};

init();
